import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listMutesRequest, storeMuteRequest, deleteMuteRequest } from '../api/chatMutesApi';
import { queryKeys } from '../query/queryKeys';
import { useAuth } from './useAuth';

/**
 * Architecture Rule 74 (extension v1.68) — per-user mute list hook.
 *
 * Plain English: keeps an in-memory `Set<userId>` of users the auth user has
 * muted, so the IncomingCallModal can check `mutedSet.has(callerId)` in O(1)
 * before deciding whether to fire the ringtone.
 *
 * The data is shared across the SPA via TanStack Query under the
 * `queryKeys.chat.mutes()` key — calling this hook from multiple places
 * costs ONE network roundtrip, not N. Updates (mute/unmute) write through
 * to the cache so the change is reflected instantly in every consumer.
 *
 * Usage:
 *   const { mutedSet, isMuted, toggle, isPending } = useUserMutes();
 *   if (isMuted(callerUserId)) suppressRingtone();
 *   <button onClick={() => toggle(otherUserId)}>Mute</button>
 */
export default function useUserMutes() {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Pull the muted user_ids from the server. Stays stale-while-revalidate
  // for 5 minutes — mute state changes are infrequent.
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.chat.mutes(),
    queryFn: async () => {
      const res = await listMutesRequest();
      const items = res?.data?.data?.items;
      return Array.isArray(items) ? items.map(Number) : [];
    },
    enabled: Boolean(isAuthenticated),
    staleTime: 5 * 60 * 1000,
  });

  const mutedSet = useMemo(() => new Set(Array.isArray(data) ? data : []), [data]);

  const muteMutation = useMutation({
    mutationFn: (userId) => storeMuteRequest(userId),
    onSuccess: (_, userId) => {
      // Optimistic-ish update — patch the cache so the next render sees it
      // without waiting for a refetch.
      queryClient.setQueryData(queryKeys.chat.mutes(), (prev = []) => {
        const next = new Set(Array.isArray(prev) ? prev : []);
        next.add(Number(userId));
        return Array.from(next);
      });
    },
  });

  const unmuteMutation = useMutation({
    mutationFn: (userId) => deleteMuteRequest(userId),
    onSuccess: (_, userId) => {
      queryClient.setQueryData(queryKeys.chat.mutes(), (prev = []) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((id) => Number(id) !== Number(userId));
      });
    },
  });

  const isMuted = useCallback(
    (userId) => mutedSet.has(Number(userId)),
    [mutedSet],
  );

  const toggle = useCallback(
    (userId) => {
      const target = Number(userId);
      if (!Number.isFinite(target) || target <= 0) return undefined;
      return mutedSet.has(target)
        ? unmuteMutation.mutateAsync(target)
        : muteMutation.mutateAsync(target);
    },
    [mutedSet, muteMutation, unmuteMutation],
  );

  return {
    mutedSet,
    isMuted,
    toggle,
    isLoading,
    isError,
    isPending: muteMutation.isPending || unmuteMutation.isPending,
  };
}
